import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

interface PatientMetricCardProps {
  icon: LucideIcon;
  accent: string;
  bgColor?: string;
  value: ReactNode;
  label: string;
  sublabel?: string;
  sublabelColor?: string;
  selected?: boolean;
  selectable?: boolean;
  onSelect?: () => void;
}

export default function PatientMetricCard({
  icon: Icon,
  accent,
  bgColor,
  value,
  label,
  sublabel,
  sublabelColor,
  selected = false,
  selectable = false,
  onSelect
}: PatientMetricCardProps) {
  const canSelect = selectable && Boolean(onSelect);
  const cardStyles = {
    p: 1.5,
    border: '1px solid',
    borderColor: selected ? accent : 'divider',
    borderRadius: 1.5,
    height: '100%',
    width: '100%',
    textAlign: 'left',
    backgroundColor: 'transparent',
    boxShadow: selected ? `0 0 15px ${accent}40` : 'none',
    cursor: canSelect ? 'pointer' : 'default',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: canSelect ? accent : 'divider',
      boxShadow: canSelect ? `0 0 15px ${accent}40` : 'none'
    }
  } as const;

  const content = (
    <Stack spacing={1.5}>
      <Box
        sx={{
          p: 1,
          width: 36,
          height: 36,
          minWidth: 36,
          borderRadius: 2,
          bgcolor: bgColor ?? alpha(accent, 0.12),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        <Icon size={20} strokeWidth={1.8} style={{ color: accent }} />
      </Box>
      <Typography variant="h4">{value}</Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
        {sublabel && (
          <Typography component="span" variant="caption" sx={{ color: sublabelColor ?? 'text.secondary', opacity: 0.7 }}>
            {' '}({sublabel})
          </Typography>
        )}
      </Typography>
    </Stack>
  );

  if (canSelect) {
    return (
      <Box
        component="button"
        type="button"
        aria-pressed={selected}
        onClick={onSelect}
        sx={cardStyles}
      >
        {content}
      </Box>
    );
  }

  return <Box sx={cardStyles}>{content}</Box>;
}